import { useExplanationSidebar } from "@coffee/contexts";
import { EvaluatedClause } from "@coffee/data-access/api-types";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { Spacing } from "@unlikelyai-magic/ui/layouts";
import { Sidebar, SidebarProps } from "@unlikelyai-magic/ui/sidebars";
import { Clause } from "./Clause";

type ExplanationSidebarProps = Pick<SidebarProps, "open" | "onClose"> & {
  allEvaluatedClauses: EvaluatedClause[];
};

export const ExplanationSidebar = ({
  allEvaluatedClauses,
  ...props
}: ExplanationSidebarProps) => {
  const { isOpen, setIsOpen } = useExplanationSidebar();

  const clausesToDisplay = allEvaluatedClauses
    .filter((clause) => clause.reasoning && clause.citation_header)
    .sort((a, b) => {
      // sort by citation_header, prioritizing those that start with a letter (e.g. "Summary" before "1.1.2")
      const aIsAlpha = /^[A-Za-z]/.test(a.citation_header);
      const bIsAlpha = /^[A-Za-z]/.test(b.citation_header);

      if (aIsAlpha && !bIsAlpha) return -1;
      if (!aIsAlpha && bIsAlpha) return 1;

      // Note - this will fail if we have something like "1.1.2" and "1.1.2a", or "1.1.8" and "1.1.10"
      // The current document structure does not have such cases, so this is acceptable for now.
      return a.citation_header.localeCompare(b.citation_header);
    });

  return (
    <Sidebar
      {...props}
      closeIconComponent={<XMarkIcon width={20} />}
      open={isOpen}
      title="Clause Explanations"
      onClose={() => setIsOpen(false)}
      width={"40%"}
    >
      <Spacing direction="vertical" gap="lg">
        {clausesToDisplay.map((clause) => (
          <Clause clause={clause} key={clause.id} />
        ))}
      </Spacing>
    </Sidebar>
  );
};
